/**
 * The letters the VIP and 等级 badges draw: open strokes on a grid 8 high
 * and 6 wide (I 2, L 5), set italic — drawn here rather than typed, so the
 * lettering is the same on every device. Shared byte-identical by both
 * consoles.
 */

const GLYPHS: Record<string, string> = {
  V: 'M0 0L3 8L6 0',
  I: 'M1 0V8',
  P: 'M0 8V0H4.8L6 1.2V3.2L4.8 4.4H0',
  L: 'M0 0V8H5',
  '0': 'M1 0H5L6 1V7L5 8H1L0 7V1Z',
  '1': 'M1.6 1.8L3.6 0V8',
  '2': 'M0 1.2L1.2 0H4.8L6 1.2V3L5 4H1L0 5V8H6',
  '3': 'M0 0H4.8L6 1.2V2.8L5.2 4H2.2M5.2 4L6 5.2V6.8L4.8 8H0',
  '4': 'M4.4 8V0L0 5.6H6',
  '5': 'M6 0H0.4V3.8H4.8L6 5V6.8L4.8 8H0',
  '6': 'M5.6 0H1.2L0 1.2V6.8L1.2 8H4.8L6 6.8V5.2L4.8 4H0',
  '7': 'M0 0H6L2.4 8',
  '8': 'M1.2 0H4.8L6 1.2V2.8L4.8 4H1.2L0 2.8V1.2ZM1.2 4H4.8L6 5.2V6.8L4.8 8H1.2L0 6.8V5.2Z',
  '9': 'M0.4 8H4.8L6 6.8V1.2L4.8 0H1.2L0 1.2V2.8L1.2 4H6',
}
const WIDTH: Record<string, number> = { I: 2, L: 5 }
// The space between two letters.
const GAP = 1.2
// How far the italic leans out at the foot, in grid units.
const LEAN = 1.41

// A glyph's path moved dx along: its M, L and H take x.
function shift(d: string, dx: number): string {
  return d.replace(/([MLHVZ])([^MLHVZ]*)/g, (_m, c: string, a: string) => {
    const v = a.trim() ? a.trim().split(/[\s,]+/).map(Number) : []
    if (c === 'M' || c === 'L') for (let i = 0; i < v.length; i += 2) v[i] = +(v[i] + dx).toFixed(2)
    else if (c === 'H') for (let i = 0; i < v.length; i++) v[i] = +(v[i] + dx).toFixed(2)
    return c + v.join(' ')
  })
}

/** The word s as one path on the grid, and how wide it is there. */
export function word(s: string) {
  let x = 0
  let d = ''
  for (const ch of s) {
    d += shift(GLYPHS[ch] ?? '', x)
    x += (WIDTH[ch] ?? 6) + GAP
  }
  return { d, w: x - GAP }
}

/** The width a word takes at letter height h, its lean included. */
export const measure = (s: string, h: number) => ((word(s).w + LEAN) * h) / 8

/**
 * The word s set italic at x, y, h high: its strokes width wide (grid
 * units) in paint — a colour, a url(#…), or a CSS variable, which goes in
 * style, where var() is read. extra is more attributes.
 */
export function letters(s: string, x: number, y: number, h: number, paint: string, width: number, extra = ''): string {
  const k = h / 8
  const ink = paint.startsWith('var(') ? `style="stroke:${paint}"` : `stroke="${paint}"`
  return `<path d="${word(s).d}" transform="translate(${(x + LEAN * k).toFixed(2)} ${y.toFixed(2)}) skewX(-10) scale(${k.toFixed(4)})" fill="none" ${ink} stroke-width="${width}" stroke-linejoin="miter" stroke-miterlimit="3"${extra}/>`
}
